import { Menu, Scissors, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ROUTES } from '@/shared/config/routes';
import { cn } from '@/shared/lib';

import { MobileNavMenu } from './MobileNavMenu';
import { NAV_LINKS } from './navLinks';

export type SiteHeaderVariant = 'marketing' | 'catalog';

type SiteHeaderProps = {
  /**
   * `marketing` floats over the hero and gains a background once scrolled;
   * `catalog` is a solid bar above the search results.
   */
  variant?: SiteHeaderVariant;
};

/** Scroll distance after which the marketing header gains its background. */
const SCROLL_THRESHOLD = 20;

export const SiteHeader = ({ variant = 'marketing' }: SiteHeaderProps) => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isCatalog = variant === 'catalog';

  useEffect(() => {
    if (isCatalog) return;

    const handleScroll = () => setIsScrolled(window.scrollY > SCROLL_THRESHOLD);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isCatalog]);

  return (
    <nav
      className={cn(
        'w-full transition-all duration-300',
        isCatalog
          ? 'relative z-50 border-b border-gray-100 bg-white py-4'
          : 'fixed top-0 right-0 left-0 z-50',
        !isCatalog &&
          isScrolled &&
          'border-b border-gray-100 bg-white/80 py-3 shadow-sm backdrop-blur-lg',
        !isCatalog && !isScrolled && 'bg-transparent py-5',
      )}
    >
      <div className="container-page flex items-center justify-between">
        <div className="flex items-center gap-2 text-primary">
          <Link to={ROUTES.home} className="flex items-center gap-2">
            <div className="rounded-lg bg-primary/10 p-2">
              <Scissors size={24} className="text-primary" />
            </div>
            <span className="font-heading text-2xl font-bold tracking-tight">{t('brand')}</span>
          </Link>
        </div>

        <div className="hidden items-center space-x-8 md:flex">
          {NAV_LINKS.map(({ to, labelKey, emphasis }) => (
            <Link
              key={to}
              to={to}
              className={cn(
                'text-gray-600 transition-colors hover:text-primary',
                emphasis ? 'font-bold' : 'font-medium',
              )}
            >
              {t(labelKey)}
            </Link>
          ))}
        </div>

        <div className="hidden items-center space-x-4 md:flex">
          <Link
            to={ROUTES.auth}
            state={{ isLogin: true }}
            className="rounded-xl px-5 py-2 font-medium text-primary transition-colors hover:bg-primary/5"
          >
            {t('nav.login')}
          </Link>
          <Link
            to={ROUTES.auth}
            state={{ isLogin: false }}
            className="transform rounded-xl bg-primary px-5 py-2 font-medium text-white shadow-glow-sm transition-all hover:-translate-y-0.5 hover:bg-primary-hover hover:shadow-[0_6px_20px_rgba(106,53,242,0.23)]"
          >
            {t('nav.createProfile')}
          </Link>
        </div>

        <button
          type="button"
          className="text-gray-800 md:hidden"
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-navigation"
          aria-label={mobileMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
          onClick={() => setMobileMenuOpen((open) => !open)}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <MobileNavMenu open={mobileMenuOpen} onNavigate={() => setMobileMenuOpen(false)} />
    </nav>
  );
};
